import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import {
  Loader2, Save, RotateCcw, Plus, Trash2, ChevronUp, ChevronDown,
  Menu as MenuIcon, ExternalLink, CornerDownRight,
} from "lucide-react";

const NAVY = "#1C4358";
const uid = () => `m_${Math.random().toString(36).slice(2, 10)}`;

const move = (arr, i, dir) => {
  const j = i + dir;
  if (j < 0 || j >= arr.length) return arr;
  const next = [...arr];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
};

const ItemRow = ({ item, onChange, onDelete, onMove, isFirst, isLast, testid, allowChildren }) => {
  const set = (k, v) => onChange({ ...item, [k]: v });
  const kids = item.children || [];
  const setKids = (next) => onChange({ ...item, children: next });
  return (
    <div className="rounded-xl border border-[#E6EDF2] bg-white p-3 space-y-3" data-testid={testid}>
      <div className="flex items-start gap-2">
        <div className="flex flex-col pt-1">
          <button type="button" aria-label="Move up" data-testid={`${testid}-up`} disabled={isFirst}
            onClick={() => onMove(-1)} className="h-6 w-6 inline-flex items-center justify-center rounded text-[#1C4358] hover:bg-[#1C4358]/10 disabled:opacity-30">
            <ChevronUp size={16} />
          </button>
          <button type="button" aria-label="Move down" data-testid={`${testid}-down`} disabled={isLast}
            onClick={() => onMove(1)} className="h-6 w-6 inline-flex items-center justify-center rounded text-[#1C4358] hover:bg-[#1C4358]/10 disabled:opacity-30">
            <ChevronDown size={16} />
          </button>
        </div>
        <div className="flex-1 grid sm:grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-[12px] font-semibold text-[#1C4358]">Label</Label>
            <Input data-testid={`${testid}-label`} value={item.label || ""} placeholder="e.g. Services"
              className="h-9 text-[14px] rounded-lg border-[#1C4358]/20 focus-visible:ring-0"
              onChange={(e) => set("label", e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-[12px] font-semibold text-[#1C4358]">Link / Path</Label>
            <Input data-testid={`${testid}-href`} value={item.href || ""} placeholder="/page, /#anchor, or https://…"
              className="h-9 text-[14px] rounded-lg border-[#1C4358]/20 focus-visible:ring-0"
              onChange={(e) => set("href", e.target.value)} />
          </div>
        </div>
        <button type="button" aria-label="Delete item" data-testid={`${testid}-delete`} onClick={onDelete}
          className="mt-6 h-8 w-8 inline-flex items-center justify-center rounded-lg text-[#9C1E22] hover:bg-[#9C1E22]/10">
          <Trash2 size={16} />
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-5 pl-8">
        <label className="flex items-center gap-2 text-[13px] font-medium text-[#4B5563]">
          <Switch data-testid={`${testid}-enabled`} checked={item.enabled !== false} onCheckedChange={(v) => set("enabled", v)} />
          Visible
        </label>
        <label className="flex items-center gap-2 text-[13px] font-medium text-[#4B5563]">
          <Switch data-testid={`${testid}-newtab`} checked={!!item.new_tab} onCheckedChange={(v) => set("new_tab", v)} />
          <ExternalLink size={13} /> Open in new tab
        </label>
      </div>

      {allowChildren && (
        <div className="pl-8 space-y-2">
          {kids.length > 0 && (
            <p className="text-[12px] font-semibold text-[#6B7280] flex items-center gap-1.5">
              <CornerDownRight size={13} /> Dropdown items
            </p>
          )}
          {kids.map((c, ci) => (
            <div key={c.id || ci} className="flex items-center gap-2" data-testid={`${testid}-child-${ci}`}>
              <Input data-testid={`${testid}-child-${ci}-label`} value={c.label || ""} placeholder="Label"
                className="h-8 text-[13px] rounded-lg border-[#1C4358]/20 focus-visible:ring-0 flex-1"
                onChange={(e) => setKids(kids.map((x, xi) => (xi === ci ? { ...x, label: e.target.value } : x)))} />
              <Input data-testid={`${testid}-child-${ci}-href`} value={c.href || ""} placeholder="/path or https://…"
                className="h-8 text-[13px] rounded-lg border-[#1C4358]/20 focus-visible:ring-0 flex-1"
                onChange={(e) => setKids(kids.map((x, xi) => (xi === ci ? { ...x, href: e.target.value } : x)))} />
              <button type="button" aria-label="Delete dropdown item" data-testid={`${testid}-child-${ci}-delete`}
                onClick={() => setKids(kids.filter((_, xi) => xi !== ci))}
                className="h-7 w-7 inline-flex items-center justify-center rounded text-[#9C1E22] hover:bg-[#9C1E22]/10 shrink-0">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          <Button type="button" size="sm" variant="outline" data-testid={`${testid}-add-child`}
            onClick={() => setKids([...kids, { id: uid(), label: "", href: "", enabled: true }])}
            className="h-7 text-[12px] gap-1">
            <Plus size={13} /> Add dropdown item
          </Button>
        </div>
      )}
    </div>
  );
};

export default function MenuEditor() {
  const [primary, setPrimary] = useState([]);
  const [footer, setFooter] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [canEdit, setCanEdit] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/site/menus");
      const withIds = (arr) => (arr || []).map((i) => ({ id: i.id || uid(), ...i, children: (i.children || []).map((c) => ({ id: c.id || uid(), ...c })) }));
      setPrimary(withIds(data.primary));
      setFooter(withIds(data.footer));
      setCanEdit(data.can_edit !== false);
    } catch (e) {
      toast.error("Could not load menus.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/site/menus", { primary, footer });
      toast.success("Menus saved. Live site updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed. Check your labels and links.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="animate-spin text-[#1C4358]" size={30} /></div>;
  }

  const Section = ({ title, note, items, setItems, allowChildren, prefix }) => (
    <Card className="p-5 border-[#E6EDF2]">
      <h2 className="text-[16px] font-display font-bold text-[#1C4358]">{title}</h2>
      <p className="text-[13px] text-[#4B5563] mb-4">{note}</p>
      <div className="space-y-3">
        {items.map((it, i) => (
          <ItemRow
            key={it.id}
            testid={`${prefix}-item-${i}`}
            item={it}
            allowChildren={allowChildren}
            isFirst={i === 0}
            isLast={i === items.length - 1}
            onMove={(dir) => setItems(move(items, i, dir))}
            onDelete={() => setItems(items.filter((_, xi) => xi !== i))}
            onChange={(next) => setItems(items.map((x, xi) => (xi === i ? next : x)))}
          />
        ))}
      </div>
      <Button type="button" variant="outline" data-testid={`${prefix}-add`}
        onClick={() => setItems([...items, { id: uid(), label: "", href: "", enabled: true, ...(allowChildren ? { children: [] } : {}) }])}
        className="mt-3 gap-1.5">
        <Plus size={15} /> Add menu item
      </Button>
    </Card>
  );

  return (
    <div className="max-w-4xl mx-auto pb-24" data-testid="menu-editor">
      <div className="sticky top-0 z-10 -mx-4 sm:-mx-6 px-4 sm:px-6 py-4 bg-white/95 backdrop-blur border-b border-[#E6EDF2] flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <MenuIcon size={20} style={{ color: NAVY }} />
            <h1 className="text-xl font-display font-bold text-[#1C4358]">Menu Editor</h1>
          </div>
          <p className="text-[13px] text-[#4B5563]">Manage the public site's main navigation &amp; footer links.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} data-testid="menu-revert" className="gap-1.5"><RotateCcw size={15} /> Revert</Button>
          <Button onClick={save} disabled={saving || !canEdit} data-testid="menu-save" className="gap-1.5 bg-[#9C1E22] hover:bg-[#861A1D] text-white">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save changes
          </Button>
        </div>
      </div>

      {!canEdit && (
        <p className="mt-4 rounded-lg bg-amber-50 border border-amber-200 px-4 py-2.5 text-[13px] text-amber-800">
          Only the Super Admin can edit site menus. You're viewing in read-only mode.
        </p>
      )}

      <div className="mt-4 space-y-4">
        <Section title="Main navigation" prefix="primary"
          note="Top header menu. Add dropdown items to create a menu with sub-links. Use “Visible” to hide without deleting."
          items={primary} setItems={setPrimary} allowChildren />
        <Section title="Footer menu" prefix="footer"
          note="The “Explore” links in the site footer."
          items={footer} setItems={setFooter} allowChildren={false} />
      </div>
    </div>
  );
}
